import { useState, useEffect, useCallback } from "react";
import { createAppointment, deleteAppointment, getProspects, getAgenda, rescheduleAppointment, rescheduleIntervention, getTeam } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { holidayOf, overlapText } from "../../../hooks/useHolidays";
import { log } from "../../../lib/log";

const syncSuffix = (d) => {
  if (d.dolibarr_event_id && !d.dolibarr_error) return " · Dolibarr synchronisé";
  if (d.dolibarr_error) return " · Dolibarr non synchronisé";
  return "";
};

const createToast = (data) => {
  if (data.dolibarr_event_id) return { title: "RDV planifié ✓", description: `Ajouté à l'agenda Dolibarr (#${data.dolibarr_event_id}).` };
  if (data.dolibarr_error) return { title: "RDV planifié (local)", description: "Synchro Dolibarr échouée: " + data.dolibarr_error, variant: "destructive" };
  return { title: "RDV planifié ✓" };
};

/* Agenda data + actions (load / add / remove / move) shared by the Appointments panel. */
export const useAgendaData = (holidays) => {
  const [data, setData] = useState({ appts: [], interventions: [], prospects: [], team: [], absences: [], doli: { events: [], error: null } });
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [p, ag, t] = await Promise.all([getProspects(), getAgenda(), getTeam().catch(() => ({ data: { users: [] } }))]);
      setData({
        prospects: p.data, team: (t.data.users || []).filter((u) => u.active !== false), appts: ag.data.local || [],
        interventions: ag.data.interventions || [], absences: ag.data.absences || [],
        doli: { events: ag.data.dolibarr?.events || [], error: ag.data.dolibarr?.error || null },
      });
      setRefreshKey((k) => k + 1);
    } catch (error) {
      log.error("Chargement de l'agenda :", error);
      toast({ title: "Agenda indisponible", description: error?.response?.data?.detail || "Réessayez dans un instant.", variant: "destructive" });
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const add = async (form) => {
    setLoading(true);
    try {
      const res = await createAppointment(form);
      load();
      toast(createToast(res.data));
      if (res.data.holiday) toast({ title: `⚠️ Jour férié : ${res.data.holiday}`, description: "Ce RDV est planifié un jour férié.", variant: "destructive" });
      return true;
    } catch (error) {
      toast({ title: "Erreur", description: error?.response?.data?.detail || "Planification impossible", variant: "destructive" });
      return false;
    } finally { setLoading(false); }
  };

  const remove = async (id) => { await deleteAppointment(id); load(); };

  const move = async (kind, id, date) => {
    try {
      const res = kind === "intervention" ? await rescheduleIntervention(id, date) : await rescheduleAppointment(id, date);
      const d = res.data;
      toast({ title: kind === "intervention" ? "Intervention replanifiée ✓" : "RDV replanifié ✓", description: `${new Date(date).toLocaleString("fr-FR")}${syncSuffix(d)}` });
      const ov = overlapText(d.warnings);
      if (ov) toast({ title: "⚠️ Chevauchement d'interventions", description: ov, variant: "destructive" });
      const hol = d.warnings?.holiday || holidayOf(holidays, date);
      if (hol) toast({ title: `⚠️ Jour férié : ${hol}`, description: "Ce créneau tombe un jour férié.", variant: "destructive" });
      load();
      return true;
    } catch (error) {
      toast({ title: "Déplacement impossible", description: error?.response?.data?.detail || "Erreur", variant: "destructive" });
      return false;
    }
  };

  return { ...data, loading, refreshKey, load, add, remove, move };
};

/* Does an agenda event belong to the selected team member? (no member = everyone) */
export const isMemberEvent = (e, member) => {
  if (!member) return true;
  if (e.kind === "rdv") return e.raw.major_login === member.login;
  if (e.kind === "intervention") return (e.raw.collaborateurs || []).some((c) => c.login === member.login || c.nom === member.nom) || e.raw.collaborateur_nom === member.nom;
  return false;
};

/* Service filter (clean / green / handy) : applies to interventions only ; RDV & Dolibarr events are hidden when a service is selected. */
export const isServiceEvent = (e, service) => {
  if (!service) return true;
  return e.kind === "intervention" && (e.raw.service || "clean") === service;
};
